import { Injectable } from '@angular/core';
import { NgxMatDateAdapter } from '@nng-components/angular-material-datetime-picker';
import { format, parseISO, isValid, startOfWeek, parse as parseDateFns } from 'date-fns';
import { enUS } from 'date-fns/locale';

@Injectable()
export class CustomNgxDateFnsAdapter extends NgxMatDateAdapter<Date> {
  protected locale = enUS;

  setLocale(locale: any) {
    this.locale = locale;
  }

  parse(value: any): Date | null {
    if (!value) return null;

    // If it's already a Date instance, validate and return
    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    const str = String(value).trim();
    if (!str) return null;

    //Try ISO first
    try {
      const iso = parseISO(str);
      if (isValid(iso)) return iso;
    } catch {}

    //Try UI input formats
    const uiFormats = ['MM/dd/yy hh:mm a', 'MM/dd/yyyy hh:mm a'];
    for (const fmt of uiFormats) {
      try {
        const d = parseDateFns(str, fmt, new Date());
        if (isValid(d)) return d;
      } catch {}
    }

    return null;
  }

  format(date: Date, displayFormat: string): string {
    if (!date || !isValid(date)) {
      return '';
    }
    // Ensure we use the full datetime format if no specific format is provided
    const formatString = displayFormat || 'MM/dd/yyyy HH:mm:ss';
    return format(date, formatString, { locale: this.locale });
  }

  clone(date: Date): Date {
    return new Date(date.getTime());
  }

  today(): Date {
    return new Date();
  }

  isValid(date: Date): boolean {
    return date instanceof Date && isValid(date);
  }

  toIso8601(date: Date): string {
    return date.toISOString();
  }

  deserialize(value: any): Date | null {
    return value ? new Date(value) : null;
  }

  getHour(date: Date): number {
    return date.getHours();
  }

  getMinute(date: Date): number {
    return date.getMinutes();
  }

  getSecond(date: Date): number {
    return date.getSeconds();
  }

  setHour(date: Date, value: number): void {
    date.setHours(value);
  }

  setMinute(date: Date, value: number): void {
    date.setMinutes(value);
  }

  setSecond(date: Date, value: number): void {
    date.setSeconds(value);
  }

  getYear(date: Date): number {
    return date.getFullYear();
  }

  getMonth(date: Date): number {
    return date.getMonth();
  }

  getDate(date: Date): number {
    return date.getDate();
  }

  setYear(date: Date, value: number): void {
    date.setFullYear(value);
  }

  setMonth(date: Date, value: number): void {
    date.setMonth(value);
  }

  setDate(date: Date, value: number): void {
    date.setDate(value);
  }

  addCalendarYears(date: Date, years: number): Date {
    return new Date(date.setFullYear(date.getFullYear() + years));
  }

  addCalendarMonths(date: Date, months: number): Date {
    return new Date(date.setMonth(date.getMonth() + months));
  }

  addCalendarDays(date: Date, days: number): Date {
    return new Date(date.setDate(date.getDate() + days));
  }

  getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }

  getDayOfWeek(date: Date): number {
    return date.getDay();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return Array.from({ length: 12 }, (_, i) =>
      format(new Date(2000, i, 1), style === 'long' ? 'MMMM' : 'MMM', { locale: this.locale }),
    );
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const baseDate = startOfWeek(new Date(2000, 0, 2));
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      return format(date, style === 'long' ? 'EEEE' : 'EEE', { locale: this.locale });
    });
  }

  getFirstDayOfWeek(): number {
    return 0; // Sunday
  }

  getNumDaysInMonth(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }

  createDate(year: number, month: number, date: number): Date {
    return new Date(year, month, date);
  }

  getYearName(date: Date): string {
    return format(date, 'yyyy', { locale: this.locale });
  }

  isDateInstance(obj: any): boolean {
    return obj instanceof Date;
  }

  invalid(): Date {
    return new Date(NaN);
  }
}
